<template>
    <component :is="template">
        <v-container>
            <div class="contents-area">
                <div class="contents-area__inner">
                    <div class="tit-wrap mb-0">
                        <h2 class="tit">상품권 구매하기</h2>
                        <div class="font-size-14 font-size-md-16 grey--text text--garken-3">
                            PG 결제를 통해 나누리 캐시 교환권을 구매하실 수 있습니다.
                        </div>
                    </div>

                    <v-divider class="my-6 my-lg-12" />
                    <purchase-method v-model="form.payment.method" @input="emit" />

                    <v-divider class="my-6 my-lg-12" />
                    <purchase-amount v-model="form" @input="emit" />

                    <template v-if="!user?.code">
                        <v-divider class="my-6 my-lg-12" />
                        <purchase-code v-model="code" />
                    </template>

                    <v-divider class="my-6 my-lg-12" />

                    <purchase-action v-model="form" v-bind="{ loading }" v-on="{ exit, post }" />
                </div>
            </div>
        </v-container>
        <common-loader v-bind="{ loading }" />
        <kcp-cert ref="kcpCert" />
    </component>
</template>

<script>
import api from "@/api";
import { mapActions, mapState } from "vuex";
import { initNshopOrder, NshopOrderAider, NSHOP_ORDER_STATES } from "@/store/nshop";

import CommonLoader from "@/components/common/common-loader.vue";
import KcpCert from "@/components/plugins/kcp/kcp-cert.vue";
import PurchaseAmount from "@/components/client/purchase/purchase-amount.vue";
import PurchaseCode from "@/components/client/purchase/purchase-code.vue";
import PurchaseAction from "@/components/client/purchase/purchase-action.vue";
import PurchaseMethod from "@/components/client/purchase/purchase-method.vue";

const defaultCount = 1;
const defaultPriceUnit = 50000;

export default {
    components: {
        CommonLoader,
        KcpCert,
        PurchaseAmount,
        PurchaseCode,
        PurchaseAction,
        PurchaseMethod,
    },
    data: () => ({
        form: (() => {
            let form = initNshopOrder({
                count: defaultCount,
                price: {
                    unit: defaultPriceUnit,
                },
                payment: {
                    method: "vaccount",
                },
            });

            form.price = NshopOrderAider.calcPrice(form);
            return form;
        })(),

        code: null,

        loading: false,
    }),
    computed: {
        ...mapState(["user"]),
        ...mapState("vaccount", { vaccount: "value" }),
        template() {
            return () => import(`@/templates/${this.$theme.pages.gshop}`);
        },
    },
    methods: {
        ...mapActions(["getUser"]),
        ...mapActions("vaccount", { getVaccount: "get", postVaccount: "post" }),

        init() {
            this.loading = false;
        },
        emit() {
            this.form = initNshopOrder(this.form);
        },
        exit() {
            this.$router.go(-1);
        },

        async validate() {
            if (!this.$store.state.payload?._user) {
                throw new Error("로그인 후 사용하실 수 있는 기능입니다.");
            }

            if (!this.user) await this.getUser();
            if (!this.user?.code) {
                if (!this.code) throw new Error("추천인 코드를 입력하세요");
                await api.v1.me.profile.put({ _id: this.user?._id, userCode: this.code });
            }

            switch (this.form.payment.method) {
                case "vaccount": {
                    await this.fetchVaccount();

                    let { summary } = await api.v1.me.nshop.orders.gets({
                        headers: { limit: 1 },
                        params: {
                            state: NSHOP_ORDER_STATES.ORDER_INIT.value,
                            ["payment.method"]: this.form.payment.method,
                        },
                    });
                    if (summary.totalCount !== 0) {
                        let go = confirm("이미 진행중인 가상계좌 결제 주문이 있습니다.\n주문을 계속하시면 기존 주문이 취소됩니다.\n\n계속하시겠습니까?");
                        if (!go) throw new Error("취소하셨습니다");
                    }
                    break;
                }
            }
        },
        async post() {
            if (this.loading) return;
            else this.loading = true;

            try {
                await this.validate();
            } catch (error) {
                alert(error.message);
                this.loading = false;
                return;
            }

            try {
                let { ...form } = this.form;

                let { order } = await api.v1.nshop.orders.post(form);

                this.$router.push({
                    path: "/purchase/done",
                    query: { _order: order._id },
                });
            } finally {
                this.loading = false;
            }
        },

        /** @returns {Promise<import('@/store/user-vaccounts').UserVaccount>} */
        async fetchVaccount() {
            await this.getVaccount();

            if (this.vaccount?._id) return;

            if (!this.user?.birthday) {
                await new Promise((resolve, reject) => {
                    this.$refs.kcpCert.$off();
                    this.$refs.kcpCert.$on("input", async ({ _cert }) => {
                        try {
                            await api.v1.me.certificate({ _cert });
                            await this.getUser();
                            resolve();
                        } catch (error) {
                            reject(error);
                        }
                    });
                    this.$refs.kcpCert.open();
                });
            }

            await this.postVaccount();
        },

        getErrorMessage({ response, message: baseMessage = "오류가 발생했습니다" }) {
            let { resultCd, resultMsg, advanceMsg } = response?.body?.result || {};

            let messages = [];

            let tag = (() => {
                let tag = [];
                if (resultCd) tag.push(resultCd);
                if (resultMsg) tag.push(resultMsg);

                if (0 < tag.length) return `[${tag.join(":")}]`;
            })();
            if (tag) messages.push(tag);
            if (advanceMsg) messages.push(tag);

            let message = messages.join("\n");
            if (!message) message = baseMessage;

            return message;
        },
    },
    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
.contents-area {
    margin: -30px -24px;
    @media (min-width: 768px) {
        margin: -40px -24px;
    }
    @media (min-width: 1024px) {
        margin: 0;
        padding: 60px 114px;
    }
    &__inner {
        @media (max-width: 1023px) {
            padding: 20px 38px;
        }
    }
}
</style>
